/**
 * TanStack Query hooks for Admin Billing (PA-2)
 *
 * Products, Catalog, Subscriptions, Orders, Pricing.
 *
 * @see docs/specifications/2026-03-08-billing-admin-ui.md
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useToast } from "../contexts/ToastContext";
import type {
  CreateProductDto,
  UpdateProductDto,
  TransitionProductStatusDto,
  AddFeatureDto,
  UpdateFeatureDto,
  CreateCatalogEntryDto,
  UpdateCatalogEntryDto,
} from "../types/adminBilling";
import {
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  transitionProductStatus,
  addProductFeature,
  updateProductFeature,
  removeProductFeature,
  listLeafProducts,
  listCatalogEntries,
  getCatalogEntry,
  createCatalogEntry,
  updateCatalogEntry,
  deleteCatalogEntry,
  listBillingSubscriptions,
  getBillingSubscription,
  reDeriveBillingSubscription,
  migrateBillingSubscription,
  updateAdminAutoRenewal,
  cancelBillingSubscription,
  listBillingOrders,
  getBillingOrder,
  cancelBillingOrder,
  registerBillingPayment,
  confirmBillingOrder,
  createBillingOrder,
  getLineDescriptionPreview,
  priceBillingOrder,
  generateBillingProforma,
  setArbitraryLinePrice,
  reversePayment,
  issueStripePaymentLink,
  listUnmatchedPayments,
  searchOrdersForAttach,
  attachPayment,
  listPricingComponents,
  getPricingComponent,
  listCalculators,
  getProductPricingPreview,
  getMigrationPreview,
  executeTenantMigration,
  type CreateBillingOrderDto,
  type MigrationPreviewDto,
  type MigrationResultDto,
} from "../services/adminProductCatalogApi";
import type { OrderStatus } from "../components/billing/OrderStatusBadge";
import { formatDate } from "../pages/admin/billing/utils/orderFormatters";

// =============================================================================
// QUERY KEYS
// =============================================================================

export const BILLING_ADMIN_KEYS = {
  products: (brandId: string) => ["billing-admin", "products", brandId] as const,
  product: (id: string) => ["billing-admin", "product", id] as const,
  leafProducts: (brandId: string) => ["billing-admin", "leaf-products", brandId] as const,
  catalogEntries: (brandId: string) =>
    ["billing-admin", "catalog", brandId] as const,
  catalogEntry: (id: string) => ["billing-admin", "catalog-entry", id] as const,
  subscriptions: (brandId: string) =>
    ["billing-admin", "subscriptions", brandId] as const,
  subscription: (id: string) =>
    ["billing-admin", "subscription", id] as const,
  orders: (brandId: string) => ["billing-admin", "orders", brandId] as const,
  order: (id: string) => ["billing-admin", "order", id] as const,
  pricing: (brandId: string) => ["billing-admin", "pricing", brandId] as const,
  pricingComponent: (id: string) =>
    ["billing-admin", "pricing-component", id] as const,
  calculators: (brandId: string) =>
    ["billing-admin", "calculators", brandId] as const,
  pricingPreview: (productId: string, brandId: string) =>
    ["billing-admin", "pricing-preview", productId, brandId] as const,
  migrationPreview: (tenantId: string) =>
    ["billing-admin", "migration-preview", tenantId] as const,
  unmatchedPayments: (params: Record<string, unknown>) =>
    ["billing-admin", "unmatched-payments", params] as const,
  orderSearch: (search: string, tenantId?: string) =>
    ["billing-admin", "order-search", search, tenantId ?? ""] as const,
  /** AC-7: deliberately excludes tenantId — the preview doesn't depend on it. */
  lineDescriptionPreview: (params: LineDescriptionPreviewQueryParams) =>
    [
      "billing-admin",
      "line-description-preview",
      params.brandId,
      params.productId,
      params.billingInterval,
      params.periodStart,
      params.customDurationDays,
    ] as const,
} as const;

// =============================================================================
// HELPERS
// =============================================================================

/** Extracts a human-readable message from an AxiosError or unknown error. */
const extractErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<{ message?: string }>;
  const message = axiosError.response?.data?.message ?? fallback;
  // L6: Truncate long error messages to avoid overflowing toast UI
  return message.slice(0, 200);
};

// =============================================================================
// PRODUCTS
// =============================================================================

export const useProductsQuery = (
  brandId: string | null,
  params: { limit?: number; offset?: number } = {}
) => {
  return useQuery({
    queryKey: [...(BILLING_ADMIN_KEYS.products(brandId ?? "")), params],
    queryFn: () => listProducts(brandId as string, params),
    enabled: !!brandId,
  });
};

export const useProductQuery = (id: string | null, brandId: string | null) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.product(id ?? ""),
    queryFn: () => getProduct(id as string, brandId as string),
    enabled: !!id && !!brandId,
  });
};

export const useCreateProductMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: CreateProductDto) => createProduct(dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.products(variables.brandId),
      });
      addToast({ message: "Produkt został utworzony", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się utworzyć produktu"),
        type: "error",
      });
    },
  });
};

export const useUpdateProductMutation = (
  productId: string,
  brandId: string,
  options?: { silent?: boolean }
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: (dto: UpdateProductDto) =>
      updateProduct(productId, brandId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.products(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.product(productId),
      });
      if (!silent) {
        addToast({ message: "Produkt został zaktualizowany", type: "success" });
      }
    },
    onError: (error: unknown) => {
      if (!silent) {
        addToast({
          message: extractErrorMessage(
            error,
            "Nie udało się zaktualizować produktu"
          ),
          type: "error",
        });
      }
    },
  });
};

export const useTransitionProductStatusMutation = (
  productId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: TransitionProductStatusDto) =>
      transitionProductStatus(productId, brandId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.products(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.product(productId),
      });
      addToast({ message: "Status produktu został zmieniony", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się zmienić statusu"),
        type: "error",
      });
    },
  });
};

export const useAddFeatureMutation = (productId: string, brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: AddFeatureDto) =>
      addProductFeature(productId, brandId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.product(productId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.products(brandId),
      });
      addToast({ message: "Feature został dodany", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się dodać feature"),
        type: "error",
      });
    },
  });
};

export const useUpdateFeatureMutation = (productId: string, brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      featureId,
      dto,
    }: {
      featureId: string;
      dto: UpdateFeatureDto;
    }) => updateProductFeature(productId, featureId, brandId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.product(productId),
      });
      addToast({ message: "Feature został zaktualizowany", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się zaktualizować feature"
        ),
        type: "error",
      });
    },
  });
};

export const useRemoveFeatureMutation = (productId: string, brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (featureId: string) =>
      removeProductFeature(productId, featureId, brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.product(productId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.products(brandId),
      });
      addToast({ message: "Feature został usunięty", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się usunąć feature"),
        type: "error",
      });
    },
  });
};

// =============================================================================
// LEAF PRODUCT PICKER (C10)
// =============================================================================

export const useLeafProductsQuery = (brandId: string | null) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.leafProducts(brandId ?? ""),
    queryFn: () => listLeafProducts(brandId as string),
    enabled: !!brandId,
  });
};

// =============================================================================
// CATALOG ENTRIES
// =============================================================================

export const useCatalogEntriesQuery = (
  brandId: string | null,
  params: { limit?: number; offset?: number } = {}
) => {
  return useQuery({
    queryKey: [...(BILLING_ADMIN_KEYS.catalogEntries(brandId ?? "")), params],
    queryFn: () => listCatalogEntries(brandId as string, params),
    enabled: !!brandId,
  });
};

export const useCatalogEntryQuery = (
  id: string | null,
  brandId: string | null
) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.catalogEntry(id ?? ""),
    queryFn: () => getCatalogEntry(id as string, brandId as string),
    enabled: !!id && !!brandId,
  });
};

export const useCreateCatalogEntryMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: CreateCatalogEntryDto) => createCatalogEntry(dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.catalogEntries(variables.brandId),
      });
      addToast({ message: "Wpis katalogowy został utworzony", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się utworzyć wpisu katalogowego"
        ),
        type: "error",
      });
    },
  });
};

export const useUpdateCatalogEntryMutation = (
  entryId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: UpdateCatalogEntryDto) =>
      updateCatalogEntry(entryId, brandId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.catalogEntries(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.catalogEntry(entryId),
      });
      addToast({
        message: "Wpis katalogowy został zaktualizowany",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się zaktualizować wpisu katalogowego"
        ),
        type: "error",
      });
    },
  });
};

export const useDeleteCatalogEntryMutation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (entryId: string) => deleteCatalogEntry(entryId, brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.catalogEntries(brandId),
      });
      addToast({ message: "Wpis katalogowy został usunięty", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się usunąć wpisu katalogowego"
        ),
        type: "error",
      });
    },
  });
};

// =============================================================================
// SUBSCRIPTIONS
// =============================================================================

export const useBillingSubscriptionsQuery = (
  brandId: string | null,
  params: {
    limit?: number;
    offset?: number;
    /** Optional tenant filter — AND-combined with brandId on the backend (AC-S1, AC-S3). */
    tenantId?: string;
    /**
     * Optional explicit `enabled` gate. Combined with `!!brandId` so callers can
     * defer the fetch until a specific tab is active (AC-C10).
     */
    enabled?: boolean;
    /** AC-6: collision check queries should not refetch on window focus. */
    refetchOnWindowFocus?: boolean;
  } = {}
) => {
  const { enabled: enabledOpt, refetchOnWindowFocus, ...queryParams } = params;
  return useQuery({
    // AC-C11: spreading params naturally yields per-tenantId cache entries.
    queryKey: [...(BILLING_ADMIN_KEYS.subscriptions(brandId ?? "")), queryParams],
    queryFn: () => listBillingSubscriptions(brandId as string, queryParams),
    enabled: !!brandId && (enabledOpt ?? true),
    placeholderData: keepPreviousData, // AC-S7
    refetchOnWindowFocus,
  });
};

export const useBillingSubscriptionQuery = (id: string | null, brandId: string) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.subscription(id ?? ""),
    queryFn: () => getBillingSubscription(id as string, brandId),
    enabled: !!id && !!brandId,
  });
};

export const useReDeriveSubscriptionMutation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (subscriptionId: string) =>
      reDeriveBillingSubscription(subscriptionId, brandId),
    onSuccess: (_data, subscriptionId) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscription(subscriptionId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscriptions(brandId),
      });
      addToast({ message: "Uprawnienia zostały zaktualizowane", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się odświeżyć uprawnień"
        ),
        type: "error",
      });
    },
  });
};

export const useMigrateSubscriptionMutation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      productId,
    }: {
      subscriptionId: string;
      productId: string;
    }) => migrateBillingSubscription(subscriptionId, productId, brandId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscription(variables.subscriptionId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscriptions(brandId),
      });
      addToast({
        message: "Subskrypcja została zmigrowana do nowego systemu",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message?: string }>;
      const status = axiosError.response?.status;
      const message =
        status === 409
          ? "Subskrypcja już została zmigrowana"
          : axiosError.response?.data?.message ?? "Nie udało się przeprowadzić migracji";
      addToast({ message, type: "error" });
    },
  });
};

export const useToggleAutoRenewalMutation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      autoRenewal,
    }: {
      subscriptionId: string;
      autoRenewal: boolean;
    }) => updateAdminAutoRenewal(subscriptionId, autoRenewal, brandId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscription(variables.subscriptionId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscriptions(brandId),
      });
      addToast({
        message: _data.autoRenewal
          ? "Automatyczne odnawianie zostało włączone"
          : "Automatyczne odnawianie zostało wyłączone",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message?: string }>;
      const status = axiosError.response?.status;
      const message =
        status === 409
          ? "Nie można zmienić ustawienia dla subskrypcji w stanie końcowym"
          : axiosError.response?.data?.message ?? "Nie udało się zmienić ustawienia automatycznego odnawiania";
      addToast({ message, type: "error" });
    },
  });
};

/**
 * AC-14c: Cancel a subscription (used from the admin UI for SCHEDULED
 * subscriptions). The endpoint also enforces AC-14b (409 when canceling an
 * ACTIVE/TRIALING subscription that has a SCHEDULED successor) — the server
 * message is surfaced as-is via extractErrorMessage, not hardcoded here.
 */
export const useCancelBillingSubscriptionMutation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: string;
      reason: string;
    }) => cancelBillingSubscription(subscriptionId, brandId, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscription(variables.subscriptionId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.subscriptions(brandId),
      });
      addToast({ message: "Subskrypcja została anulowana", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się anulować subskrypcji"),
        type: "error",
      });
    },
  });
};

// =============================================================================
// ORDERS
// =============================================================================

export const useBillingOrdersQuery = (
  brandId: string | null,
  params: {
    limit?: number;
    offset?: number;
    /** Optional tenant filter — AND-combined with brandId on the backend (AC-O1, AC-O4). */
    tenantId?: string;
    /** Multi-select status filter (AC-6) — e.g. open-order collision checks. */
    status?: OrderStatus[];
    /** Optional gate for lazy tab fetch (AC-C10). */
    enabled?: boolean;
    /** AC-6: collision check queries should not refetch on window focus. */
    refetchOnWindowFocus?: boolean;
  } = {}
) => {
  const { enabled: enabledOpt, refetchOnWindowFocus, ...queryParams } = params;
  return useQuery({
    queryKey: [...(BILLING_ADMIN_KEYS.orders(brandId ?? "")), queryParams],
    queryFn: () => listBillingOrders(brandId as string, queryParams),
    enabled: !!brandId && (enabledOpt ?? true),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus,
  });
};

export const useBillingOrderQuery = (
  id: string | null,
  brandId: string | null
) => {
  return useQuery({
    queryKey: [...BILLING_ADMIN_KEYS.order(id ?? ""), brandId ?? ""],
    queryFn: () => getBillingOrder(id as string, brandId as string),
    enabled: !!id && !!brandId,
  });
};

export const useCancelBillingOrderMutation = (
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (cancelReason: string) =>
      cancelBillingOrder(orderId, brandId, cancelReason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.orders(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      addToast({ message: "Zamówienie zostało anulowane", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się anulować zamówienia"
        ),
        type: "error",
      });
    },
  });
};

export const useRegisterBillingPaymentMutation = (
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (params: {
      amountCents: number;
      source: string;
      receivedAt: string;
      documentId: string;
      externalReference?: string;
    }) =>
      registerBillingPayment(orderId, { brandId, ...params }),
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.orders(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      addToast({ message: "Płatność została zarejestrowana", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się zarejestrować płatności"
        ),
        type: "error",
      });
    },
  });
};

export const useConfirmBillingOrderMutation = (
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () => confirmBillingOrder(orderId, brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.orders(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      addToast({
        message: "Zamówienie zostało potwierdzone i zrealizowane",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się potwierdzić zamówienia"
        ),
        type: "error",
      });
    },
  });
};

export const useCreateBillingOrderMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: CreateBillingOrderDto) => createBillingOrder(dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.orders(variables.brandId),
      });
      addToast({ message: "Zamówienie utworzone", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się utworzyć zamówienia"),
        type: "error",
      });
    },
  });
};

/**
 * AC-6/AC-7: live preview of the order-line description + derived period end
 * for the manual order form. Caller is responsible for debouncing `periodStart`
 * / `customDurationDays` and for computing `enabled` (product selected, valid
 * date, valid custom days) — this hook only wires the (already-debounced)
 * params into React Query.
 *
 * `customDurationDays` is `null` (not omitted) in the key when the custom-length
 * switch is off — the key must stay a stable 7-element tuple so the switch's on/off
 * states don't accidentally collide or thrash the cache.
 *
 * No `tenantId` in the key (preview doesn't depend on it), no `keepPreviousData`
 * (AC-7: must never show a stale description/periodEnd for different params),
 * `retry: false` (errors should surface immediately — precedent `:1054`).
 */
export interface LineDescriptionPreviewQueryParams {
  brandId: string;
  productId: string;
  billingInterval: "monthly" | "quarterly" | "yearly";
  periodStart: string;
  customDurationDays: number | null;
}

export const useLineDescriptionPreviewQuery = (
  params: LineDescriptionPreviewQueryParams,
  options: { enabled: boolean }
) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.lineDescriptionPreview(params),
    queryFn: ({ signal }) =>
      getLineDescriptionPreview(
        {
          brandId: params.brandId,
          productId: params.productId,
          billingInterval: params.billingInterval,
          periodStart: params.periodStart,
          ...(params.customDurationDays != null
            ? { customDurationDays: params.customDurationDays }
            : {}),
        },
        signal
      ),
    enabled: options.enabled,
    retry: false,
  });
};

export const usePriceBillingOrderMutation = (
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () => priceBillingOrder(orderId, brandId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      const netPln = new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
        currencyDisplay: "code",
        maximumFractionDigits: 2,
      }).format(data.totalNetCents / 100);
      addToast({
        message: `Zamówienie wycenione: ${netPln} netto`,
        type: "success",
      });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Nie udało się wycenić zamówienia"),
        type: "error",
      });
    },
  });
};

export const useGenerateBillingProformaMutation = (
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    // AC-4: optional manual due date override.
    mutationFn: (dueDate?: string) =>
      generateBillingProforma(orderId, brandId, dueDate),
    onSuccess: (_data, dueDate) => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      addToast({
        message: dueDate
          ? `Proforma wygenerowana, termin płatności ${formatDate(dueDate)}`
          : "Proforma wygenerowana",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się wygenerować proformy"
        ),
        type: "error",
      });
    },
  });
};

// =============================================================================
// PAYMENT REVERSAL (AC-A4)
// =============================================================================

/**
 * Reverse a recorded payment for a given order.
 *
 * On success: invalidates order cache, emits "Płatność cofnięta" toast.
 * On 409 REVERSAL_TOO_LATE: caller should retry with force=true (owner only).
 */
export const useReversePaymentMutation = (
  paymentId: string,
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (params: { reason: string; force?: boolean }) =>
      reversePayment(paymentId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.orders(brandId),
      });
      addToast({ message: "Płatność cofnięta", type: "success" });
    },
    // onError: caller (ReversePaymentModal) handles 409 REVERSAL_TOO_LATE specially —
    // no generic toast here to avoid double-toast on force-flow.
  });
};

// =============================================================================
// PHASE B — STRIPE PAYMENT LINK (AC-B1)
// =============================================================================

/**
 * Issue a Stripe Payment Link for a DRAFT order (AC-B1).
 * Returns { url, sessionId, expiresAt }.
 *
 * onError mapping (UX HIGH fix):
 *  - 409 PROFORMA_REQUIRED → "Najpierw wystaw proformę"
 *  - 409 ORDER_NOT_DRAFT   → "Można wystawić tylko dla zamówień w statusie DRAFT"
 *  - 422 (Stripe timeout)  → "Stripe nie odpowiada. Spróbuj ponownie."
 *  - default               → "Nie udało się utworzyć linku Stripe: <message>"
 */
export const useIssueStripePaymentLinkMutation = (
  orderId: string,
  brandId: string
) => {
  const { addToast } = useToast();
  return useMutation({
    mutationFn: () => issueStripePaymentLink(orderId, brandId),
    // No cache invalidation needed — link is ephemeral (expiresAt).
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message?: string }>;
      const status = axiosError.response?.status;
      const backendMessage = axiosError.response?.data?.message ?? "";

      let message: string;
      if (status === 409 && backendMessage.includes("PROFORMA_REQUIRED")) {
        message = "Najpierw wystaw proformę";
      } else if (status === 409 && backendMessage.includes("ORDER_NOT_DRAFT")) {
        message = "Można wystawić tylko dla zamówień w statusie DRAFT";
      } else if (status === 422) {
        message = "Stripe nie odpowiada. Spróbuj ponownie.";
      } else {
        message = `Nie udało się utworzyć linku Stripe: ${extractErrorMessage(error, "wystąpił błąd")}`;
      }
      addToast({ message, type: "error" });
    },
  });
};

// =============================================================================
// PHASE B — UNMATCHED PAYMENTS (AC-B4)
// =============================================================================

/**
 * Query for unmatched payments list page.
 * Default: receivedAfter = 30 days ago (L-16, R5).
 */
export const useUnmatchedPaymentsQuery = (params: {
  receivedAfter?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) => {
  const { enabled: enabledOpt, ...queryParams } = params;
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.unmatchedPayments(queryParams as Record<string, unknown>),
    queryFn: () => listUnmatchedPayments({ matchStatus: "UNMATCHED", ...queryParams }),
    enabled: enabledOpt ?? true,
    placeholderData: keepPreviousData,
  });
};

/**
 * Debounced order search for AttachPaymentModal (C-10).
 * enabled=false when query is empty (avoid empty search requests).
 */
export const useOrderSearchQuery = (params: {
  search: string;
  tenantId?: string;
  brandId?: string;
  enabled?: boolean;
}) => {
  const { enabled: enabledOpt, ...queryParams } = params;
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.orderSearch(queryParams.search, queryParams.tenantId),
    queryFn: () =>
      searchOrdersForAttach({
        search: queryParams.search,
        tenantId: queryParams.tenantId,
        brandId: queryParams.brandId,
        limit: 10,
      }),
    enabled: (enabledOpt ?? true) && queryParams.search.trim().length >= 2,
    staleTime: 10_000,
  });
};

/**
 * Attach an unmatched payment to an order (AC-B4).
 * On 200: invalidate unmatched payments list, show "Płatność przypięta" toast.
 * On 409: caller shows friendly message.
 */
export const useAttachPaymentMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      paymentId,
      orderId,
      documentId,
    }: {
      paymentId: string;
      orderId: string;
      documentId?: string | null;
    }) => attachPayment(paymentId, { orderId, documentId }),
    onSuccess: () => {
      // Invalidate entire unmatched-payments cache (all param variants)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "billing-admin" &&
          query.queryKey[1] === "unmatched-payments",
      });
      addToast({ message: "Płatność przypięta", type: "success" });
    },
    // onError: caller (AttachPaymentModal) handles 409 specially for friendly messages.
  });
};

// =============================================================================
// PRICING
// =============================================================================

export const usePricingComponentsQuery = (
  brandId: string | null,
  params: { limit?: number; offset?: number } = {}
) => {
  return useQuery({
    queryKey: [...(BILLING_ADMIN_KEYS.pricing(brandId ?? "")), params],
    queryFn: () => listPricingComponents(brandId as string, params),
    enabled: !!brandId,
  });
};

export const usePricingComponentQuery = (id: string | null, brandId: string | null) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.pricingComponent(id ?? ""),
    queryFn: () => getPricingComponent(id as string, brandId as string),
    enabled: !!id && !!brandId,
  });
};

export const useCalculatorsQuery = (
  brandId: string | null,
  params: { limit?: number; offset?: number } = {}
) => {
  return useQuery({
    queryKey: [...(BILLING_ADMIN_KEYS.calculators(brandId ?? "")), params],
    queryFn: () => listCalculators(brandId as string, params),
    enabled: !!brandId,
  });
};

// =============================================================================
// PRICING PREVIEW
// =============================================================================

export const usePricingPreviewQuery = (
  productId: string | null,
  brandId: string | null,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: BILLING_ADMIN_KEYS.pricingPreview(productId ?? "", brandId ?? ""),
    queryFn: () =>
      getProductPricingPreview(productId as string, brandId as string),
    enabled: (options?.enabled ?? true) && !!productId && !!brandId,
  });
};

// =============================================================================
// TENANT BILLING MIGRATION (AC-A5, AC-A6, AC-A7)
// =============================================================================

/**
 * Fetch migration preview for a tenant.
 * Enabled only when tenantId is provided and enabled=true.
 * Uses long staleTime (5 min) matching backend cache TTL.
 */
export const useMigrationPreviewQuery = (
  tenantId: string | null,
  options?: { enabled?: boolean }
) => {
  return useQuery<MigrationPreviewDto, Error>({
    queryKey: BILLING_ADMIN_KEYS.migrationPreview(tenantId ?? ""),
    queryFn: () => getMigrationPreview(tenantId as string),
    enabled: (options?.enabled ?? true) && !!tenantId,
    staleTime: 5 * 60 * 1000, // Match backend cache TTL (5 min)
    retry: false, // Don't retry — errors should be shown immediately
  });
};

/**
 * Execute the tenant billing migration.
 * On success, invalidates the tenants list to refresh migration status.
 */
export const useExecuteTenantMigrationMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation<MigrationResultDto, Error, string>({
    mutationFn: (tenantId: string) => executeTenantMigration(tenantId),
    onSuccess: (_result: MigrationResultDto) => {
      // Invalidate tenant list to reflect migration status
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      addToast({
        message: "Migracja zakończona pomyślnie",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      const message = extractErrorMessage(error, "Błąd migracji. Spróbuj ponownie.");
      addToast({ message, type: "error" });
    },
  });
};

export const useSetArbitraryPriceMutation = (
  orderId: string,
  brandId: string
) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (params: {
      lineId: string;
      netCents: number;
      arbitraryReason: string;
    }) =>
      setArbitraryLinePrice({
        orderId,
        brandId,
        lineId: params.lineId,
        netCents: params.netCents,
        arbitraryReason: params.arbitraryReason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BILLING_ADMIN_KEYS.order(orderId),
      });
      addToast({ message: "Cena linii zapisana", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(
          error,
          "Nie udało się zapisać ceny linii"
        ),
        type: "error",
      });
    },
  });
};

// Re-export types for consumers
export type {
  CreateBillingOrderDto,
  MigrationPreviewDto,
  MigrationResultDto,
};
